import React, { useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Toolbar,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  BusinessCenterOutlined as JobsIcon,
  Close as CloseIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  PeopleAltOutlined as CandidatesIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import CandidateTrackingPage from "../components/CandidateTrackingPage";
import ManageJobBoard from "../components/ManageJobBoard";
import PostNewJOB from "../components/PostNewJob";
import hireSmartLogo from "../layout/hiresmart-logo.png";
type PageId = "jobs" | "create" | "candidates";
const BRAND_TEAL = "#0F766E";

const PAGE_ITEMS: Array<{
  id: PageId;
  title: string;
  shortTitle: string;
  description: string;
  icon: React.ReactNode;
}> = [
  {
    id: "jobs",
    title: "Job Board",
    shortTitle: "Jobs",
    description: "View and manage job postings",
    icon: <JobsIcon />,
  },
  {
    id: "create",
    title: "Create Job",
    shortTitle: "Create Job",
    description: "Create a new job posting",
    icon: <AddIcon />,
  },
  {
    id: "candidates",
    title: "Candidate Applications",
    shortTitle: "Candidates",
    description: "Review and compare applicants",
    icon: <CandidatesIcon />,
  },
];

const HRDashboard: React.FC = () => {
  const [activePage, setActivePage] = useState<PageId>("jobs");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [candidateJob, setCandidateJob] = useState<{
    id?: string;
    status?: string;
  }>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const currentPage =
    PAGE_ITEMS.find((item) => item.id === activePage) || PAGE_ITEMS[0];

  const changePage = (page: PageId) => {
    setActivePage(page);
    setDrawerOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openCandidates = (jobId: string, status: string) => {
    setCandidateJob({ id: jobId, status });
    changePage("candidates");
  };

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    navigate("/login");
  };

  const renderPage = () => {
    if (activePage === "create") return <PostNewJOB />;
    if (activePage === "candidates") {
      return (
        <CandidateTrackingPage
          initialJobId={candidateJob.id}
          initialJobStatus={candidateJob.status}
        />
      );
    }
    return (
      <ManageJobBoard
        onCreateJob={() => changePage("create")}
        onViewCandidates={openCandidates}
      />
    );
  };

  const navigation = (
    <List sx={{ p: 1 }} aria-label="HR workspace navigation">
      {PAGE_ITEMS.map((item) => (
        <ListItemButton
          key={item.id}
          selected={activePage === item.id}
          onClick={() => changePage(item.id)}
          sx={{
            minHeight: 50,
            borderRadius: 1.5,
            mb: 0.5,
            "&.Mui-selected": {
              bgcolor: alpha(BRAND_TEAL, 0.1),
              color: BRAND_TEAL,
              "&:hover": { bgcolor: alpha(BRAND_TEAL, 0.14) },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 42, color: "inherit" }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.title}
            secondary={item.description}
            primaryTypographyProps={{
              fontWeight: activePage === item.id ? 750 : 600,
            }}
            secondaryTypographyProps={{ fontSize: 12 }}
          />
        </ListItemButton>
      ))}
    </List>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "grey.50" }}>
      <AppBar
        position="fixed"
        elevation={0}
        color="inherit"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "rgba(255,255,255,.96)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar
            sx={{
              minHeight: { xs: 64, md: 72 },
              px: { xs: 1.5, sm: 2.5, md: 3 },
            }}
          >
            {isMobile && (
              <IconButton
                edge="start"
                aria-label="Open navigation"
                onClick={() => setDrawerOpen(true)}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{ minWidth: 0 }}
            >
              <Box
                component="img"
                src={hireSmartLogo}
                alt="HireSmart logo"
                sx={{
                  width: { xs: 112, sm: 150 },
                  height: 80,
                  objectFit: "contain",
                  objectPosition: "left center",
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  component="h1"
                  noWrap
                  sx={{
                    fontWeight: 750,
                    lineHeight: 1.2,
                    fontSize: { xs: "1rem", sm: "1.15rem" },
                    pl: { xs: 1, sm: 1.5 },
                    borderLeft: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {currentPage.title}
                </Typography>
              </Box>
            </Stack>

            {!isMobile && (
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ ml: 5 }}
                component="nav"
                aria-label="HR workspace"
              >
                {PAGE_ITEMS.map((item) => (
                  <Button
                    key={item.id}
                    startIcon={item.icon}
                    onClick={() => changePage(item.id)}
                    aria-current={activePage === item.id ? "page" : undefined}
                    sx={{
                      minHeight: 42,
                      px: 2,
                      color:
                        activePage === item.id ? BRAND_TEAL : "text.secondary",
                      bgcolor:
                        activePage === item.id
                          ? alpha(BRAND_TEAL, 0.08)
                          : "transparent",
                      fontWeight: activePage === item.id ? 750 : 600,
                      textTransform: "none",
                      borderRadius: 1.5,
                    }}
                  >
                    {item.shortTitle}
                  </Button>
                ))}
              </Stack>
            )}

            <Box sx={{ flexGrow: 1 }} />
            <Button
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              color="inherit"
              aria-label="Sign out of HireSmart"
              sx={{
                minHeight: 42,
                color: "text.secondary",
                textTransform: "none",
              }}
            >
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                Sign out
              </Box>
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { width: 300 } }}
      >
        <Toolbar sx={{ minHeight: 64, px: 2 }}>
          <Typography variant="h6" fontWeight={750} sx={{ flexGrow: 1 }}>
            HireSmart
          </Typography>
          <IconButton
            aria-label="Close navigation"
            onClick={() => setDrawerOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
        <Divider />
        {navigation}
      </Drawer>

      <Container
        maxWidth="xl"
        component="main"
        sx={{ pt: { xs: 10, md: 12 }, pb: 5, px: { xs: 1.5, sm: 2.5, md: 3 } }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2.5, md: 3 },
            minHeight: "70vh",
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            borderRadius: 2.5,
          }}
        >
          {renderPage()}
        </Paper>
      </Container>
    </Box>
  );
};

export default HRDashboard;